import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const HeroWebGL = () => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    let scene, camera, renderer, points;
    const isMobile = window.innerWidth <= 768;

    const widthCount = isMobile ? 35 : 75;
    const heightCount = isMobile ? 35 : 75;
    const pointsCount = widthCount * heightCount;
    const spacing = isMobile ? 0.35 : 0.22;

    const positions = new Float32Array(pointsCount * 3);
    const initialPositions = new Float32Array(pointsCount * 3);

    // Set initial grid positions
    for (let x = 0; x < widthCount; x++) {
      for (let z = 0; z < heightCount; z++) {
        const idx = (x * heightCount + z) * 3;
        // X Position
        positions[idx] = (x - widthCount / 2) * spacing;
        // Y Position (Height - initial flat)
        positions[idx + 1] = -0.5; 
        // Z Position
        positions[idx + 2] = (z - heightCount / 2) * spacing;

        // Save initial coordinates for relative wave/mouse updates
        initialPositions[idx] = positions[idx];
        initialPositions[idx + 1] = positions[idx + 1];
        initialPositions[idx + 2] = positions[idx + 2];
      }
    }

    const w = containerRef.current.clientWidth;
    const h = containerRef.current.clientHeight;

    scene = new THREE.Scene();
    
    // Position camera looking down at the grid with a slight angle
    camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 100);
    camera.position.set(0, 4.5, 7.5);
    camera.lookAt(0, -0.5, 0);

    renderer = new THREE.WebGLRenderer({ canvas: canvasRef.current, antialias: true, alpha: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    // Particle texture creator
    const canvas = document.createElement('canvas');
    canvas.width = 16;
    canvas.height = 16;
    const ctx = canvas.getContext('2d');
    const grad = ctx.createRadialGradient(8, 8, 0, 8, 8, 8);
    grad.addColorStop(0, 'rgba(255, 255, 255, 1)');
    grad.addColorStop(0.4, 'rgba(249, 115, 22, 0.5)'); // Soft orange ring
    grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 16, 16);
    const texture = new THREE.CanvasTexture(canvas);

    const material = new THREE.PointsMaterial({
      size: isMobile ? 0.14 : 0.08,
      map: texture,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });

    points = new THREE.Points(geometry, material);
    scene.add(points);

    // Mouse Tracking (Only on desktop)
    const mouse = { x: -9999, y: -9999, targetX: -9999, targetY: -9999 };
    const raycaster = new THREE.Raycaster();
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0.5); // flat plane matching the grid height

    const handleMouseMove = (e) => {
      if (isMobile) return;
      // Map mouse coordinates to normalized device coordinates (-1 to +1)
      mouse.targetX = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.targetY = -(e.clientY / window.innerHeight) * 2 + 1;
    };

    if (!isMobile) {
      window.addEventListener('mousemove', handleMouseMove);
    }

    // Animation Loop
    let clock = new THREE.Clock();
    let animationFrameId;

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      const time = clock.getElapsedTime() * 0.8;
      const posArr = geometry.attributes.position.array;
      let mouseWorldPos = new THREE.Vector3(-9999, -9999, -9999);

      // Raycast mouse position onto the horizontal plane
      if (!isMobile && mouse.targetX !== -9999) {
        // Interpolate mouse movement for smooth delay
        mouse.x += (mouse.targetX - mouse.x) * 0.1;
        mouse.y += (mouse.targetY - mouse.y) * 0.1;

        raycaster.setFromCamera(new THREE.Vector2(mouse.x, mouse.y), camera);
        raycaster.ray.intersectPlane(plane, mouseWorldPos);
      }

      // Loop through all points in grid
      for (let i = 0; i < pointsCount; i++) {
        const i3 = i * 3;
        const initX = initialPositions[i3];
        const initZ = initialPositions[i3 + 2];

        // 1. Calculate dynamic waves using trigonometric math
        // Combination of sine and cosine waves to create a natural topographical mesh ripple
        let waveHeight = Math.sin(initX * 0.35 + time) * 0.25;
        waveHeight += Math.cos(initZ * 0.35 + time * 1.2) * 0.25;
        waveHeight += Math.sin((initX + initZ) * 0.15 + time * 0.5) * 0.15;

        // 2. Mouse displacement calculation (only if cursor is active)
        if (!isMobile && mouseWorldPos.x !== -9999) {
          const pt = new THREE.Vector3(initX, -0.5, initZ);
          const dist = pt.distanceTo(mouseWorldPos);
          const influenceRadius = 2.0;

          if (dist < influenceRadius) {
            // Smooth bell curve displacement
            const force = (1.0 - dist / influenceRadius) * 0.8;
            waveHeight += force; // lift points near mouse cursor
          }
        }

        // Apply updated height
        posArr[i3 + 1] = -0.5 + waveHeight;
      }

      geometry.attributes.position.needsUpdate = true;

      // Slowly rotate the entire grid for cinematic perspective shifting
      points.rotation.y = Math.sin(time * 0.05) * 0.08;

      renderer.render(scene, camera);
    };

    animate();

    // Resize Handler
    const handleResize = () => {
      if (!containerRef.current) return;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      if (renderer) {
        renderer.dispose();
      }
      if (geometry) {
        geometry.dispose();
      }
      if (material) {
        material.dispose();
      }
      if (texture) {
        texture.dispose();
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="absolute inset-0 w-full h-full pointer-events-none z-0 overflow-hidden">
      <canvas ref={canvasRef} className="w-full h-full block opacity-60" />
    </div>
  );
};

export default HeroWebGL;
